import config from "src/config";
import { ResponseDto } from "src/dtos/common/responseDto";
import {
  PDA1040_R01SearchCondition,
  PDA1040_R01HeaderDto,
  PDA1040_R01DetailDto,
  PDA1040_R01ItemDto,
  PDA1040_R01CheckRequest,
  PDA1040_R01TransportRequest,
  PDA1040_R01DefectRequest,
  PDA1040_R01Response,
} from "src/containers/PDA/PDA1040_R01/PDA1040_R01Dto";
import { getData, postData } from "src/utils/axios/serverHelper";

/**
 * PDA1040_R01 입고품목 PDA 화면 API
 * 백엔드 엔드포인트: /api/v1/PDA1040_R01
 */
const purchaseOrderUrl = `${config.apiUrl}/PDA1040_R01`;

/**
 * 1. 발주 목록 조회 (좌측 그리드)
 * 
 * 날짜 범위 또는 발주번호로 발주 목록을 조회합니다.
 * 입고 가능한 발주만 조회됩니다 (상태: C=확정, P=진행중, 마감여부: N).
 * 
 * @param condition 검색 조건 (시작일, 종료일, 발주번호, 거래처, 물류센터 등)
 * @returns 발주 목록
 */
export const getPurchaseOrderList = async (condition: PDA1040_R01SearchCondition) => {
  return await getData<ResponseDto<PDA1040_R01HeaderDto[]>>(`${purchaseOrderUrl}/orders`, {
    params: condition,
  });
};

/**
 * 2. 발주 헤더 단건 조회 (우측 상단)
 * 
 * 발주번호로 발주 헤더 정보를 조회합니다.
 * 
 * @param purchaseOrderNo 발주번호
 * @returns 발주 헤더 정보
 */
export const getPurchaseOrderHeader = async (purchaseOrderNo: string) => {
  const url = `${purchaseOrderUrl}/orders/${purchaseOrderNo}`;
  return await getData<ResponseDto<PDA1040_R01HeaderDto>>(url);
};

/**
 * 3. 발주 상세 목록 조회 (하단 그리드)
 * 
 * 발주번호로 발주 상세 목록을 조회합니다.
 * 각 품목별 발주량, 입고량, 미입고량이 포함됩니다.
 * 
 * @param purchaseOrderNo 발주번호
 * @returns 발주 상세 목록
 */
export const getPurchaseOrderDetails = async (purchaseOrderNo: string) => {
  const url = `${purchaseOrderUrl}/orders/details/${purchaseOrderNo}`;
  return await getData<ResponseDto<PDA1040_R01DetailDto[]>>(url);
};

/**
 * 4. 바코드 스캔 품목 조회
 * 
 * 스캔한 바코드로 품목 정보를 조회하고, 해당 발주에 포함되어 있는지 검증합니다.
 * 
 * @param barcode 스캔한 바코드
 * @param purchaseOrderNo 발주번호
 * @returns 품목 정보 (발주 포함 여부, 미입고량 포함)
 */
export const scanBarcode = async (barcode: string, purchaseOrderNo: string) => {
  return await getData<ResponseDto<PDA1040_R01ItemDto>>(`${purchaseOrderUrl}/scan`, {
    params: {
      barcode,
      purchaseOrderNo,
    },
  });
};

/**
 * 5-1. 검수 등록
 * 
 * 스캔한 품목의 검수를 등록합니다 (WMS Order checkQty만 업데이트).
 * 
 * 처리 내용:
 * - WMS Order 생성/업데이트 (checkQty만)
 * - 재고 이동 없음
 * 
 * @param data 검수 등록 요청
 * @returns 처리 결과
 */
export const checkTransport = async (data: PDA1040_R01CheckRequest) => {
  return await postData<PDA1040_R01CheckRequest, ResponseDto<PDA1040_R01Response>>(
    `${purchaseOrderUrl}/check`,
    data
  );
};

/**
 * 5-2. 입고 완료
 * 
 * 검수된 품목의 입고를 완료합니다 (Transport → Stock → 매입).
 * 
 * 처리 내용:
 * - WMS Order transportQty 업데이트
 * - Transport (입고 전표) 생성
 * - 재고 증가
 * - 매입 실적 생성
 * - 발주 상태 업데이트 (모든 품목 입고 완료 시)
 * 
 * @param data 입고 완료 요청
 * @returns 처리 결과
 */
export const registerTransport = async (data: PDA1040_R01TransportRequest) => {
  return await postData<PDA1040_R01TransportRequest, ResponseDto<PDA1040_R01Response>>(
    `${purchaseOrderUrl}/transport`,
    data
  );
};

/**
 * 5-3. 불량 처리
 * 
 * 불량 품목을 처리합니다 (itemStatus='C').
 * 
 * 처리 내용:
 * - WMS Order 업데이트
 * - Transport (불량 전표) 생성
 * - 불량 재고로 등록
 * 
 * @param data 불량 처리 요청
 * @returns 처리 결과
 */
export const registerDefect = async (data: PDA1040_R01DefectRequest) => {
  return await postData<PDA1040_R01DefectRequest, ResponseDto<PDA1040_R01Response>>(
    `${purchaseOrderUrl}/defect`,
    data
  );
};
